/**
 * Endpoint 10 — POST fileupload:storefront (research/04 §5.10; REQ-API-8).
 * Verbatim port of common_utils.js uploadFileToAPI + the delegated change
 * handler initializeFileUploadHandlers (registered once per render —
 * duplicate registrations preserved, research/02 §12.7).
 */
import { appState } from "../state/app-state";
import { globalHelper_ReportError } from "./logs-error";
import { extensionLabels, fileExtension, isAllowedFile } from "../elements/file-types";
import { dirAttr, t } from "../elements/base";
import { readableAccent, withAlpha } from "../elements/colour";
import {
  showEmpty,
  showFailed,
  showUploaded,
  showUploading,
  UPLOAD_FAILED_STATE,
} from "../elements/drop-zone-state";
import $ from "jquery";

/**
 * One source string with two tokens, not a sentence built from pieces: t() looks
 * up a whole phrase, and word order moves between languages. Registered in the
 * API's AppPhrases so it reaches the merchant's translation table.
 */
const WRONG_TYPE_MESSAGE =
  "Sorry, {filetype} files aren't accepted. Please upload one of: {allowed}";

/** Same inline-error node CheckTextInputValidity uses, so ResetInputs clears both. */
function showRefusal(fileInput: any, fileName: string, allowed: string[]): void {
  clearRefusal(fileInput);
  const message = t(WRONG_TYPE_MESSAGE)
    .replace("{filetype}", fileExtension(fileName).replace(".", "").toUpperCase())
    .replace("{allowed}", extensionLabels(allowed));

  const anchor = fileInput.previousElementSibling?.classList?.contains("cpc-drop-zone")
    ? fileInput.previousElementSibling
    : fileInput;
  anchor.insertAdjacentHTML(
    "afterend",
    "<label class='cpc-text-error' style='color:red; display:block; margin-top:4px;'" +
      dirAttr(WRONG_TYPE_MESSAGE) +
      ">" +
      message +
      "</label>",
  );
  $(fileInput).addClass("error-border");
}

function clearRefusal(fileInput: any): void {
  $(fileInput).parent().find(".cpc-text-error").remove();
  $(fileInput).removeClass("error-border");
}

// Helper function to upload a file and return the URL (with retry + abort support)
// abortSignal: optional AbortSignal — if signalled, retries are cancelled immediately
export async function uploadFileToAPI(
  file: any,
  propertyName: any,
  statusSelector: any,
  fileInput?: any,
  abortSignal?: AbortSignal,
): Promise<any> {
  const fileUploadApiUrl = appState.baseCalculatorNewApiUrl + "fileupload:storefront";
  const statusSpan = $(statusSelector);
  const maxRetries = 3;
  const backoffDelays = [1000, 2000, 4000];

  // Show uploading spinner
  statusSpan
    .html(
      `
    <svg width="16" height="16" viewBox="0 0 16 16" style="vertical-align: middle;">
      <circle cx="8" cy="8" r="6" fill="none" stroke="#0066cc" stroke-width="2"
              stroke-dasharray="30" stroke-linecap="round">
        <animateTransform attributeName="transform" type="rotate" from="0 8 8" to="360 8 8"
                          dur="1s" repeatCount="indefinite"/>
      </circle>
    </svg>
  `,
    )
    .css("display", "inline-block")
    .show();

  let lastError: any = null;

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    // Bail out immediately if a newer upload has been triggered for this input
    if (abortSignal && abortSignal.aborted) {
      console.warn(
        `Upload for "${propertyName}" aborted (superseded by new file selection).`,
      );
      return null;
    }

    try {
      // Fresh FormData per attempt — avoids body-stream-consumed issues in some browsers
      const formData = new FormData();
      formData.append("file", file);
      formData.append("calculatorId", appState.cpc_calculatorId);

      const response = await fetch(fileUploadApiUrl, {
        method: "POST",
        body: formData,
        signal: abortSignal,
      });

      if (!response.ok) {
        throw new Error(`Upload failed with status: ${response.status}`);
      }

      const data = await response.json();

      if (data.fileUrl) {
        // Show success checkmark
        statusSpan.html(`
          <svg width="16" height="16" viewBox="0 0 16 16" style="vertical-align: middle;">
            <circle cx="8" cy="8" r="7" fill="#28a745"/>
            <path d="M5 8 L7 10 L11 6" stroke="white" stroke-width="2" fill="none"
                  stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        `);

        setTimeout(() => {
          statusSpan.fadeOut();
        }, 3000);

        return data.fileUrl;
      } else {
        throw new Error("No fileUrl in response");
      }
    } catch (error: any) {
      // If aborted (new file selected mid-retry), stop silently — don't show error UX
      if (error.name === "AbortError") {
        console.warn(
          `Upload for "${propertyName}" aborted mid-request (superseded by new file selection).`,
        );
        return null;
      }

      lastError = error;
      console.warn(
        `File upload attempt ${attempt}/${maxRetries} failed for "${propertyName}":`,
        error.message,
      );

      if (attempt < maxRetries) {
        // Wait for backoff delay, but exit early if aborted during the wait
        await new Promise((resolve, reject) => {
          const timer = setTimeout(resolve, backoffDelays[attempt - 1]);
          if (abortSignal) {
            abortSignal.addEventListener(
              "abort",
              () => {
                clearTimeout(timer);
                reject(new DOMException("Aborted", "AbortError"));
              },
              { once: true },
            );
          }
        }).catch((err) => {
          if (err.name === "AbortError") {
            console.warn(`Upload retry wait for "${propertyName}" aborted.`);
            return null; // will be caught by outer catch on next iteration check
          }
          throw err;
        });

        // Re-check abort after the wait
        if (abortSignal && abortSignal.aborted) {
          return null;
        }
      }
    }
  }

  // All retries exhausted — show persistent error
  console.error("File upload failed after all retries:", lastError);
  globalHelper_ReportError(lastError, "File upload failed for " + propertyName);

  // Clear the file input so validation blocks ATC and user can re-select
  if (fileInput) {
    fileInput.value = "";
  }

  // Show persistent error message (no auto-fade — stays until user picks a new file)
  statusSpan
    .html(
      `
    <span style="display: inline-flex; align-items: center; gap: 4px;">
      <svg width="16" height="16" viewBox="0 0 16 16" style="vertical-align: middle;">
        <circle cx="8" cy="8" r="7" fill="#dc3545"/>
        <path d="M5 5 L11 11 M11 5 L5 11" stroke="white" stroke-width="2"
              stroke-linecap="round"/>
      </svg>
      <span style="color: #dc3545; font-size: 13px;">${t(UPLOAD_FAILED_STATE)}</span>
    </span>
  `,
    )
    .css("display", "inline-block")
    .show();

  throw lastError;
}

// Tracks the active AbortController per upload input (keyed by property name)
// so that selecting a new file cancels any in-flight retries for that input.
const _uploadAbortControllers: Record<string, AbortController> = {};

/**
 * The drop zone is a <label for>, so clicking it already opens the picker with no
 * JS at all. Dropping is what needs wiring: the file has to be pushed into the
 * real input so the same change handler, the same validation and the same cart
 * property serve both paths — there is no second upload route for dropped files.
 */
function initializeDropZoneHandlers(): void {
  $(document)
    .on("dragover dragenter", ".cpc-drop-zone", function (event: any) {
      event.preventDefault();
      const styling = appState.calculatorStyling;
      // Same guard as the browse link: an accent equal to the background would
      // make the drag-over state indistinguishable from the resting one.
      const accent = readableAccent(
        styling.priceBackground,
        styling.upperBackground,
        styling.upperTextColor,
      );
      $(this).css({
        "border-style": "solid",
        "border-color": accent,
        background: withAlpha(accent, 0.1),
      });
    })
    .on("dragleave drop", ".cpc-drop-zone", function (event: any) {
      event.preventDefault();
      const text = appState.calculatorStyling.upperTextColor;
      $(this).css({
        "border-style": "dashed",
        "border-color": withAlpha(text, 0.28),
        background: withAlpha(text, 0.04),
      });
    })
    .on("drop", ".cpc-drop-zone", function (event: any) {
      const dropped = event.originalEvent?.dataTransfer?.files?.[0];
      if (!dropped) return;

      const input = document.getElementById(
        $(this).attr("for") ?? "",
      ) as HTMLInputElement | null;
      if (!input) return;

      // Assigning through a DataTransfer is the only way to set input.files;
      // the change event then runs the single upload path below.
      const transfer = new DataTransfer();
      transfer.items.add(dropped);
      input.files = transfer.files;
      input.dispatchEvent(new Event("change", { bubbles: true }));
    });
}

/**
 * Remove puts the element back to empty. Clearing the hidden URL is the part that
 * matters beyond appearance: ValidateInputs reads it, so a required element starts
 * blocking add-to-cart again the moment the shopper takes their file away.
 */
function initializeRemoveHandler(): void {
  $(document).on("click", ".cpc-drop-zone__remove", function () {
    const propertyName = $(this).closest(".cpc-drop-zone__file").data("property-name");
    const input = document.querySelector(
      `input.cpc-file-upload[data-property-name="${propertyName}"]`,
    ) as HTMLInputElement | null;
    if (input) input.value = "";
    $(`input.cpc-file-url-input[data-property-name="${propertyName}"]`).val("");
    $(`.cpc-drop-zone__file[data-property-name="${propertyName}"]`)
      .parent()
      .find(".cpc-text-error")
      .remove();
    showEmpty(propertyName);
  });
}

export function initializeFileUploadHandlers(): void {
  initializeDropZoneHandlers();
  initializeRemoveHandler();

  // Use event delegation on the document to handle dynamically added file inputs
  $(document)
    .on("change", 'input[name^="cpc_upload_fileupload_"]', async function (event: any) {
      const fileInput = event.target;
      const $fileInput = $(fileInput);
      const file = fileInput.files[0];

      if (!file) {
        return;
      }

      const propertyName = $fileInput.data("property-name");

      // DIV-012: refuse a disallowed type here, before any network call. The
      // accept attribute only filters the OS picker — a shopper can switch it to
      // "All files", and drag-and-drop ignores it outright.
      const accept = $fileInput.attr("accept");
      const allowed = accept ? accept.split(",") : [];
      if (!isAllowedFile(file.name, allowed)) {
        showRefusal(fileInput, file.name, allowed);
        showEmpty(propertyName);
        fileInput.value = "";
        $(
          `input.cpc-file-url-input[data-property-name="${propertyName}"]`,
        ).val("");
        return;
      }
      clearRefusal(fileInput);
      showUploading(propertyName, file.name);

      // The drop zone carries its own status inside the file row, so the button
      // style's span must stay out of it entirely — left active it painted a second
      // tick above the row that then faded on its own. A detached node absorbs the
      // spinner/tick/fade and the persistent failure text without rendering any of
      // it, which keeps uploadFileToAPI unchanged for the button style.
      const ownsStatus = $(
        `.cpc-drop-zone__file[data-property-name="${propertyName}"]`,
      ).length > 0;
      const statusSpan = ownsStatus
        ? $("<span></span>")
        : $fileInput.siblings(".cpc-upload-status");
      const hiddenInput = $(
        `input.cpc-file-url-input[data-property-name="${propertyName}"]`,
      );

      // Cancel any in-flight upload/retry for this input before starting a new one
      if (_uploadAbortControllers[propertyName]) {
        _uploadAbortControllers[propertyName]!.abort();
      }
      const controller = new AbortController();
      _uploadAbortControllers[propertyName] = controller;

      // Mark upload in progress and clear any stale URL
      $fileInput.attr("data-cpc-uploading", "true");
      hiddenInput.val("");

      try {
        const fileUrl = await uploadFileToAPI(
          file,
          propertyName,
          statusSpan,
          fileInput,
          controller.signal,
        );
        // fileUrl is null if the upload was aborted (superseded) — don't update hidden input
        if (fileUrl !== null) {
          hiddenInput.val(fileUrl);
          showUploaded(propertyName, file.name, file.size, fileUrl);
        }
      } catch (error) {
        hiddenInput.val("");
        showFailed(propertyName, file.name);
      } finally {
        // Clean up controller reference and uploading flag
        if (_uploadAbortControllers[propertyName] === controller) {
          delete _uploadAbortControllers[propertyName];
        }
        $fileInput.removeAttr("data-cpc-uploading");
      }
    });
}
